/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useMemo, useRef, useState } from 'react';
import { useAnalytics } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  makeStyles,
  useMediaQuery,
  Link as MuiLink,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { buildClients } from './clients';
import type { EditorGroup, EditorTab, McpServerDescriptor } from './types';
import { useTypedText } from './useTypedText';

export type PanelAnalytics = { copy: string; switch: string; variant: string; connect: string };
export type McpInstallPanelProps = {
  server: McpServerDescriptor;
  /** Subject = `${prefix}:${id}` — the banner passes its historical prefixes. */
  analytics: PanelAnalytics;
  /** Namespace for the tab/tabpanel DOM ids. */
  idPrefix: string;
  /** 'auto' = two columns from the lg VIEWPORT (banner); 'stacked' = always one
   * column (the drawer is narrow even on wide screens). */
  layout?: 'auto' | 'stacked';
  /** Opens on this client's tab (and app, inside a multi-client vendor) when
   * the server lists it, e.g. the catalog's "Works with" filter. */
  initialClient?: string;
};

export const BANNER_ANALYTICS: PanelAnalytics = {
  copy: 'ai-editor-setup',
  switch: 'ai-editor-tab',
  variant: 'ai-editor-variant',
  connect: 'ai-editor-connect',
};

const useStyles = makeStyles(theme => ({
  tabs: {
    minHeight: 32,
    marginTop: theme.spacing(0.5),
  },
  // MUI v4 hides the scroll arrows below the xs breakpoint (Tabs.js: `display:
  // none` under `breakpoints.down('xs')`, applied whenever scrollButtons !==
  // 'on') — i.e. on precisely the narrow viewports where the row overflows, and
  // `variant="scrollable"` hides the native scrollbar too, so a mouse-only user
  // is left with no affordance at all. Restore them by pointer type rather than
  // width: a touch user swipes the row, and below ~350px (the only widths where
  // it overflows) arrows would take 80px of it for nothing, while a fine
  // pointer has nothing else to click. This sheet is injected after MuiTabs, so
  // it wins on source order where the query matches.
  scrollArrows: {
    '@media (hover: hover) and (pointer: fine)': {
      display: 'flex',
    },
  },
  tab: {
    minHeight: 32,
    minWidth: 0,
    padding: theme.spacing(0.5, 1.5),
    textTransform: 'none',
    fontSize: '0.8125rem',
    // MUI's idle tab ink is off-palette (a light blue on the dark theme); the
    // selected tab keeps its brand colour via the higher-specificity rule.
    color: theme.palette.text.secondary,
    // 210px of labels + 8px a side = 274px, inside the 297px the full-bleed
    // strip gets at 375 (and 282 at 360), so no arrow is needed.
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(0.5, 1) },
  },
  // The app picker and/or setup toggle, stacked. It sits closer to the copy it
  // changes than to the strip, so the grouping reads panel-first.
  variants: {
    gridArea: 'variants',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    // Twice the air above than below: measured flush against the tab strip and
    // 12px off its own copy, which grouped it with the row it must NOT be
    // mistaken for. It governs the text beneath it, so it belongs to it.
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  // Which app is a segmented control — the portal's own (Claude Usage uses it
  // for granularity) — so the three levels read as three kinds of control:
  // accent-underlined vendor tabs, boxed app choice, then the ink-underlined
  // setup toggle. Selection is fill and ink, not weight, so choosing never
  // changes a segment's width.
  appPicker: {
    '& button': {
      textTransform: 'none',
      fontSize: '0.75rem',
      lineHeight: 1.6,
      // ~29px tall: clear of the 24px target floor without outgrowing the
      // 32px vendor tabs above it.
      padding: theme.spacing(0.5, 1.25),
      // Lab's idle ink is action.active at 38% — 3.4:1 on the dark tint.
      color: theme.palette.text.secondary,
    },
    '& button.Mui-selected': { color: theme.palette.text.primary },
  },
  // The setup toggle is an underlined row that must not read as a nested tab
  // set, so subordination is carried three ways: smaller type, a 1px ink rule
  // where the strip uses 2px accent, and emphasis by weight, not colour. Under
  // an app picker it also hangs off a rule, marking it as a choice within the
  // app picked above; the rule is text.disabled, not divider, which at 1.3:1
  // vanished on the light theme's cream.
  toggleGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    '$appPicker + &': {
      borderLeft: `1px solid ${theme.palette.text.disabled}`,
      paddingLeft: theme.spacing(2),
    },
  },
  variantButton: {
    font: 'inherit',
    fontSize: '0.75rem',
    lineHeight: 1.6,
    letterSpacing: 0,
    color: theme.palette.text.secondary,
    padding: theme.spacing(0, 0, 0.25),
    // Carried on both states so choosing never shifts the row by a pixel.
    borderBottom: '1px solid transparent',
    // The rule has to stay ~2px off the text to read as a tab indicator, so the
    // target grows by an overlay rather than by padding: the 22px text box plus
    // 5px top and bottom clears the 24px minimum and lands on the 32px the
    // portal's own filter controls use next door. Vertical only, so the 16px
    // gap keeps the two targets from ever touching.
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -5,
      right: 0,
      bottom: -5,
      left: 0,
    },
    transition: theme.transitions.create(['color', 'border-bottom-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      color: theme.palette.text.primary,
      borderBottomColor: theme.palette.divider,
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 3,
    },
  },
  variantButtonOn: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderBottomColor: theme.palette.text.primary,
  },
  // Stacked on ordinary widths; from lg the pitch (body/note/links) sits
  // left and the terminal fills the right half, so a wide banner has no
  // dead column and roughly halves its height. DOM order stays the stacked
  // order (variants, body, terminal, note, links) — grid areas do the move.
  panel: {
    display: 'grid',
    gridTemplateAreas: '"variants" "body" "terminal" "note" "links"',
    [theme.breakpoints.up('lg')]: {
      gridTemplateAreas:
        '"variants terminal" "body terminal" "note terminal" "links terminal"',
      gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
      gridTemplateRows: 'auto auto auto 1fr',
      columnGap: theme.spacing(3),
    },
  },
  // The drawer is ~480px even on a wide screen, where the lg two-column grid
  // would squeeze the terminal; stacked keeps the phone order at every width.
  panelStacked: {
    [theme.breakpoints.up('lg')]: {
      gridTemplateAreas: '"variants" "body" "terminal" "note" "links"',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gridTemplateRows: 'auto',
    },
  },
  text: {
    gridArea: 'body',
    // Primary tone because this IS the instruction. It was secondary, which
    // washed out the one paragraph the reader must follow and left nothing but
    // a 1px size step between it and the note beneath — the gap the note's
    // italic had been covering for.
    color: theme.palette.text.primary,
    fontSize: '0.8125rem',
    marginTop: theme.spacing(0.5),
    // Keeps the measure readable (~90ch at 13px) when stacked; in the
    // two-column layout the grid column bounds it first.
    maxWidth: 720,
  },
  // -- The terminal ---------------------------------------------------------
  // A dark surface in both themes: this is a window into the CLI, not a
  // themed panel. Warm near-blacks sit with the portal's cream/red world.
  terminal: {
    gridArea: 'terminal',
    marginTop: theme.spacing(1),
    maxWidth: 720,
    [theme.breakpoints.up('lg')]: {
      // Top-aligned with the body text; the column sets the width.
      marginTop: theme.spacing(0.5),
      maxWidth: 'none',
      alignSelf: 'start',
    },
    borderRadius: 6,
    border: `1px solid ${
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(46,32,28,0.28)'
    }`,
    backgroundColor: '#221B18',
    boxShadow: '0 2px 8px rgba(46, 32, 28, 0.18)',
    overflow: 'hidden',
  },
  chrome: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 0.75, 0.25, 1.25),
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  dots: {
    display: 'flex',
    gap: 5,
    flexShrink: 0,
    '& > span': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.22)',
    },
  },
  chromeTitle: {
    flex: 1,
    fontFamily: 'monospace',
    fontSize: '0.6875rem',
    letterSpacing: '0.02em',
    color: 'rgba(245, 240, 235, 0.62)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  copy: {
    padding: theme.spacing(0.5),
    flexShrink: 0,
    color: 'rgba(245, 240, 235, 0.72)',
    '&:hover': {
      color: '#F5F0EB',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  screen: {
    padding: theme.spacing(1, 1.5),
  },
  commandLine: {
    display: 'block',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.6,
    color: '#F5F0EB',
    // Terminals wrap; prefer word boundaries and shear identifiers only
    // when a single token exceeds the line (overflow-wrap, not break-all).
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  // The prompt is CSS generated content, not DOM text, so neither the
  // blocked-clipboard Range selection nor a manual mouse copy ever picks
  // up the `$ ` — copied text is always exactly the paste-safe snippet.
  line: {
    '&::before': {
      content: 'attr(data-prompt)',
      color: '#FF9B85',
    },
  },
  caret: {
    display: 'inline-block',
    width: '0.55em',
    height: '1.05em',
    marginLeft: 1,
    verticalAlign: 'text-bottom',
    backgroundColor: '#FF9B85',
    animation: '$blink 1.1s steps(2, start) infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes blink': {
    to: { visibility: 'hidden' },
  },
  // The response row is ALWAYS rendered (a space placeholder when idle) so a
  // copy fills text into reserved space instead of growing the terminal and
  // shifting the whole page below it.
  responseLine: {
    display: 'block',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.6,
    whiteSpace: 'pre',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  responseCopied: {
    color: '#8FD6A8',
    animation: '$settle 160ms ease-out',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  responseBlocked: {
    color: '#E8C07A',
    animation: '$settle 160ms ease-out',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes settle': {
    from: { opacity: 0, transform: 'translateY(2px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  // Not italic: these notes run to three or four lines, and a long italic run
  // is markedly harder to read than the upright it is set against. The size
  // step and the secondary tone already subordinate it — the slant was doing
  // the same job a third time, and paying for it in legibility.
  note: {
    gridArea: 'note',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    lineHeight: 1.55,
    marginTop: theme.spacing(1),
  },
  requires: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  actions: {
    gridArea: 'links',
    alignSelf: 'start',
    marginTop: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Guides and docs read as references, not as rival actions: one primary
  // button (1-Click Connect, or the terminal's copy) and quiet links beside it.
  moreLinks: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    '& a': { fontWeight: 500 },
  },
}));

type CopyState = 'idle' | 'copied' | 'blocked';

export const McpInstallPanel = ({ server, analytics, idPrefix, layout = 'auto', initialClient }: McpInstallPanelProps) => {
  const { groups } = useMemo(() => buildClients(server), [server]);
  if (groups.length === 0) return null;
  return (
    <McpInstallPanelContent
      groups={groups}
      analytics={analytics}
      idPrefix={idPrefix}
      layout={layout}
      initialClient={initialClient}
    />
  );
};

const McpInstallPanelContent = ({ groups, analytics, idPrefix, layout, initialClient }: Omit<McpInstallPanelProps, 'server'> & { groups: EditorGroup[] }) => {
  const classes = useStyles();
  const capture = useAnalytics();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const codeRef = useRef<HTMLElement>(null);
  const initialGroup = initialClient
    ? groups.findIndex(g => g.clients.some(c => c.id === initialClient))
    : -1;
  const [tabIndex, setTabIndex] = useState(Math.max(initialGroup, 0));
  // The client picked inside each multi-client vendor tab, so leaving Claude
  // and coming back returns to Desktop if that is where the reader was.
  const [clientByGroup, setClientByGroup] = useState<Record<string, string>>(
    () =>
      initialGroup >= 0 && initialClient
        ? { [groups[initialGroup].id]: initialClient }
        : {},
  );
  const [variantId, setVariantId] = useState<string | undefined>(undefined);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  const clientIn = (g: EditorGroup) =>
    g.clients.find(c => c.id === clientByGroup[g.id]) ?? g.clients[0];
  const safeIndex = Math.min(tabIndex, groups.length - 1);
  const group = groups[safeIndex];
  const base = clientIn(group);
  // Keyed by id, not index: a tab without the remembered id falls back to its
  // default, while Claude Code and Codex share ids on purpose — "no marketplace
  // access" is true of the reader, not of the tab.
  const variant =
    base.variants?.find(v => v.id === variantId) ?? base.variants?.[0];
  const tab: EditorTab = variant ? { ...base, ...variant.overrides } : base;
  // Distinguishes the two setups in analytics; without it the uptake question
  // this toggle exists to answer would be unanswerable.
  const analyticsId = variant ? `${base.id}:${variant.id}` : base.id;
  const { shown, skip } = useTypedText(tab.snippet, !reducedMotion);

  const copy = async () => {
    skip();
    try {
      await navigator.clipboard.writeText(tab.snippet);
      setCopyState('copied');
      capture.captureEvent('copy', `${analytics.copy}:${analyticsId}`);
    } catch {
      // Clipboard unavailable (permissions, non-secure context): select the
      // snippet so one keystroke finishes the job, and say so on-screen.
      const node = codeRef.current;
      const selection = window.getSelection();
      if (node && selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setCopyState('blocked');
    }
  };

  const toggleButton = (
    key: string,
    label: string,
    on: boolean,
    onSelect: () => void,
  ) => (
    <ButtonBase
      key={key}
      disableRipple
      aria-label={label}
      aria-pressed={on}
      className={`${classes.variantButton} ${on ? classes.variantButtonOn : ''}`}
      onClick={() => {
        if (on) return;
        onSelect();
        setCopyState('idle');
      }}
    >
      {label}
    </ButtonBase>
  );

  const responseText =
    copyState === 'copied'
      ? tab.copiedLine
      : copyState === 'blocked'
        ? '✗ clipboard blocked — text selected, press ⌘C / Ctrl+C'
        : ' ';

  return (
    <>
        <Tabs
          className={classes.tabs}
          value={safeIndex}
          onChange={(_e, v) => {
            setTabIndex(v);
            setCopyState('idle');
            // Reported by client, not vendor, so the subjects stay the ones
            // recorded before Claude Code and Desktop shared a tab.
            capture.captureEvent(
              'switch',
              `${analytics.switch}:${clientIn(groups[v]).id}`,
            );
          }}
          indicatorColor="primary"
          textColor="primary"
          // Below ~350px the four tabs overflow even with the phone layout's
          // tighter padding, and MUI v4 clips rather than scrolls unless asked. Only
          // `variant` is load-bearing — scrollButtons is already "auto" by
          // default (Tabs.js) and is stated for the reader; classes.scrollArrows
          // is what actually makes the arrows reachable on small screens.
          variant="scrollable"
          scrollButtons="auto"
          classes={{ scrollButtonsDesktop: classes.scrollArrows }}
          aria-label="AI editor"
        >
          {groups.map(g => (
            <Tab
              key={g.id}
              id={`${idPrefix}-tab-${g.id}`}
              aria-controls={`${idPrefix}-tabpanel`}
              className={classes.tab}
              label={g.label}
            />
          ))}
        </Tabs>
        <div
          role="tabpanel"
          id={`${idPrefix}-tabpanel`}
          aria-labelledby={`${idPrefix}-tab-${group.id}`}
          className={`${classes.panel} ${layout === 'stacked' ? classes.panelStacked : ''}`}
        >
          {(group.clients.length > 1 || base.variants) && (
            <Box className={classes.variants}>
              {group.clients.length > 1 && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  className={classes.appPicker}
                  value={base.id}
                  onChange={(_e, id: string | null) => {
                    // Exclusive groups emit null when the pressed one is
                    // clicked again; staying put is the right answer.
                    if (!id) return;
                    setClientByGroup(prev => ({ ...prev, [group.id]: id }));
                    setCopyState('idle');
                    capture.captureEvent('switch', `${analytics.switch}:${id}`);
                  }}
                  aria-label={`${group.label} app`}
                >
                  {group.clients.map(c => (
                    <ToggleButton key={c.id} value={c.id} aria-label={c.label}>
                      {c.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              {base.variants && (
                <Box
                  role="group"
                  aria-label={`${base.label} setup`}
                  className={classes.toggleGroup}
                >
                  {base.variants.map(v =>
                    toggleButton(v.id, v.label, v.id === variant?.id, () => {
                      setVariantId(v.id);
                      capture.captureEvent(
                        'click',
                        `${analytics.variant}:${base.id}:${v.id}`,
                      );
                    }),
                  )}
                </Box>
              )}
            </Box>
          )}
          <Typography component="div" className={classes.text}>
            {tab.body}
          </Typography>
          {/* Any focus entering the terminal (e.g. tabbing to the copy
              button) completes the typing — keyboard users never wait. */}
          <Box className={classes.terminal} onFocusCapture={skip}>
            <Box className={classes.chrome}>
              <Box className={classes.dots} aria-hidden="true">
                <span />
                <span />
                <span />
              </Box>
              <Typography component="div" className={classes.chromeTitle}>
                {tab.chromeTitle}
              </Typography>
              <Tooltip
                title={
                  copyState === 'copied' ? 'Copied' : `Copy ${tab.label} setup`
                }
              >
                <IconButton
                  className={classes.copy}
                  size="small"
                  aria-label={`Copy ${tab.label} setup`}
                  onClick={copy}
                >
                  {copyState === 'copied' ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <FileCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
            {/* Clicking the screen fast-forwards the typing — a terminal
                affordance, not a control; the copy button is the action. */}
            <Box className={classes.screen} onClick={skip}>
              <Typography
                component="code"
                ref={codeRef}
                aria-label={`${tab.chromeTitle}: ${tab.snippet}`}
                className={classes.commandLine}
              >
                {shown.split('\n').map((line, i, lines) => (
                  <React.Fragment key={i}>
                    <span
                      className={classes.line}
                      data-prompt={tab.prompt || undefined}
                    >
                      {line}
                    </span>
                    {i === lines.length - 1 ? (
                      <span className={classes.caret} aria-hidden="true" />
                    ) : (
                      '\n'
                    )}
                  </React.Fragment>
                ))}
              </Typography>
              <Typography
                component="code"
                role="status"
                className={`${classes.responseLine} ${
                  copyState === 'copied'
                    ? classes.responseCopied
                    : copyState === 'blocked'
                      ? classes.responseBlocked
                      : ''
                }`}
              >
                {responseText}
              </Typography>
            </Box>
          </Box>
          {(tab.note || tab.requires) && (
            <Typography component="div" className={classes.note}>
              {tab.requires && (
                <div className={classes.requires}>Requires: {tab.requires}</div>
              )}
              {tab.note}
            </Typography>
          )}
          <Box className={classes.actions}>
            {tab.connect && (
              <Tooltip title={tab.connect.hint}>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  href={tab.connect.href}
                  // A custom scheme hands off to the OS and leaves the page
                  // alone; an https hand-off page would replace Backstage.
                  {...(tab.connect.href.startsWith('https:') && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                  onClick={() =>
                    capture.captureEvent(
                      'click',
                      `${analytics.connect}:${analyticsId}`,
                    )
                  }
                >
                  {tab.connect.label}
                </Button>
              </Tooltip>
            )}
            {tab.links.length > 0 && (
              <Typography component="span" className={classes.moreLinks}>
                {tab.links.map((l, i) => (
                  <React.Fragment key={l.href}>
                    {i > 0 && ' · '}
                    <MuiLink href={l.href} target="_blank" rel="noopener noreferrer" color="primary">
                      {l.label}
                    </MuiLink>
                  </React.Fragment>
                ))}
              </Typography>
            )}
          </Box>
        </div>
    </>
  );
};
